import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useStateCallback, isDefined, isFunction, useKeyPress, isUndefined, useMergedRef } from '@sravni/react-utils';
import { Dropdown } from '../../Dropdown/src/Dropdown.js';
import '../../Space/src/Space.js';
import '../../Textarea/src/Textarea.js';
import '../../Typography/src/Heading.js';
import '../../Typography/src/Link.js';
import '../../Typography/src/ListItem.js';
import '../../Typography/src/OrderedList.js';
import '../../Typography/src/Text.js';
import '../../Typography/src/UnorderedList.js';
import { useOptionsMap } from '../hooks/useOptionsMap.js';
import modules_b8cc600d from '../styles/Autocomplete.module.less.js';
import { EmptyContent } from './EmptyContent.js';
import { IconElement } from './IconElement.js';
import { InputView } from './InputView.js';

const MIN_ROWS = 1;
// TODO мобильный вид требует доработки
const mobile = false;
/**
 * @description The Autocomplete component allow user to type and select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Autocomplete placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
// eslint-disable-next-line max-statements
const Autocomplete = React.forwardRef((props, ref) => {
    const { defaultValue, value, options, autoFocus, invalid, disabled, className, menuClassName, mode, icon, readOnly, placement = 'bottom-start', fixedWidth = true, resizable = false, emptyContent, title, closeOnSelect = true, flip = true, enableCustomValue = false, optionsSort = true, fixedHeight, loading, onChange, onSelect, onSearch, onClear, onFocus, onBlur, ...restProps } = props;
    const cx = useStyles(modules_b8cc600d);
    const isFirstRender = React.useRef(true);
    const inputRef = React.useRef(null);
    const menuRef = React.useRef(null);
    const formControlContext = useFormControlContext();
    const [visible, setVisible] = useBoolean(false);
    const [focused, setFocused] = useBoolean(false);
    const [fixWidth, setFixWidth] = useBoolean(fixedWidth);
    const [innerValue, setInnerValue] = useStateCallback(value ?? defaultValue);
    const [inputValue, setInputValue] = React.useState('');
    const [innerOptions, setInnerOptions] = React.useState(options || []);
    const [selected, setSelected] = React.useState(false);
    const getOptionByValue = useOptionsMap(options);
    const isMultiple = mode === 'multiple';
    const displayValue = React.useMemo(() => {
        if (isMultiple) {
            if (visible && !selected)
                return inputValue;
            if (!Array.isArray(innerValue) || !innerValue.length)
                return '';
            return innerValue
                .map((v) => getOptionByValue(v)?.label)
                .filter(Boolean)
                .join(', ');
        }
        if (visible) {
            return inputValue;
        }
        const { label } = getOptionByValue(innerValue);
        const newLabel = enableCustomValue && label !== inputValue ? inputValue : label;
        return newLabel;
    }, [isMultiple, enableCustomValue, selected, visible, inputValue, innerValue, getOptionByValue]);
    const setValue = React.useCallback((newValue) => {
        setInnerValue(newValue, (val) => {
            let label = '';
            if (!isMultiple) {
                label = getOptionByValue(val).label;
                setInputValue(label || val);
            }
            else {
                label = Array.isArray(val) ? val.map((v) => getOptionByValue(v)?.label).join(', ') : '';
            }
            onChange?.(label ?? '');
            onSelect?.(val ?? '');
        });
    }, [isMultiple, setInnerValue, getOptionByValue, onSelect, onChange]);
    const closeMenuOnSelect = React.useCallback((value) => (isDefined(closeOnSelect) && isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect), [closeOnSelect]);
    const handleSelect = React.useCallback((value) => {
        setValue(value);
        isMultiple && setSelected(true);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [isMultiple, closeMenuOnSelect, setValue, setVisible]);
    const handleClick = React.useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const handleChangeInput = React.useCallback((event) => {
        const { value } = event.currentTarget;
        setInputValue(value);
        if (onSearch === undefined) {
            setInnerOptions(options.filter((o) => o.label.toLowerCase().indexOf(value.toLowerCase()) >= 0));
        }
        onChange?.(value);
        onSearch?.(value);
        !visible && inputRef.current?.click();
    }, [onSearch, onChange, visible, options]);
    const handleFocusInput = React.useCallback((event) => {
        if (!focused) {
            const { label } = getOptionByValue(innerValue);
            setFocused.on();
            if (!enableCustomValue) {
                setInputValue(label);
            }
            onFocus?.(event);
        }
    }, [focused, enableCustomValue, innerValue, getOptionByValue, onFocus, setFocused]);
    const handleBlurInput = React.useCallback((event) => {
        if (menuRef.current?.contains(event.relatedTarget)) {
            return;
        }
        const { label } = getOptionByValue(innerValue);
        if (enableCustomValue && label !== inputValue) {
            setInnerValue(undefined);
        }
        onBlur?.(event);
        setVisible.off();
        setFocused.off();
        setInnerOptions(options);
    }, [
        getOptionByValue,
        innerValue,
        enableCustomValue,
        inputValue,
        onBlur,
        setVisible,
        setFocused,
        options,
        setInnerValue,
    ]);
    const handleClickClear = React.useCallback((event) => {
        event.stopPropagation();
        setValue('');
        onClear?.();
        inputRef.current?.focus();
    }, [setValue, onClear]);
    const handleEmptyContentClick = React.useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const handleClose = React.useCallback(() => {
        setVisible.off();
    }, [setVisible]);
    const header = React.useMemo(() => {
        {
            return null;
        }
    }, [title, displayValue, visible, innerValue, icon, handleClickClear, resizable, handleChangeInput]);
    useKeyPress({
        // закрывает выпадающее меню по Enter в случае с произвольным вводом
        enabled: !!enableCustomValue && (visible || focused) && !isMultiple && !innerOptions.length,
        keyMap: {
            Enter: (event) => {
                event.preventDefault();
                closeMenuOnSelect(innerValue) && inputRef.current?.click();
            },
        },
    }, [innerValue, innerOptions.length, visible, focused, enableCustomValue, isMultiple, closeMenuOnSelect]);
    const defaultModeProps = React.useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        className: cx('textarea', { 'with-icon': !(icon === null) && !resizable, focus: focused || visible }),
        readOnly: readOnly || mobile || selected || (!visible && isMultiple),
        invalid: formControlContext.invalid || invalid,
        disabled: formControlContext.disabled || disabled,
        value: displayValue,
        icon: React.createElement(IconElement, { visible: visible, value: innerValue, icon: icon, handleClickClear: handleClickClear }),
        resizable,
        rows: MIN_ROWS,
        wrap: resizable ? 'soft' : 'off',
        loading,
        onChange: handleChangeInput,
        onFocus: handleFocusInput,
        onBlur: handleBlurInput,
    }), [
        cx,
        icon,
        resizable,
        focused,
        visible,
        readOnly,
        selected,
        isMultiple,
        formControlContext.invalid,
        formControlContext.disabled,
        invalid,
        disabled,
        displayValue,
        innerValue,
        handleClickClear,
        loading,
        handleChangeInput,
        handleFocusInput,
        handleBlurInput,
    ]);
    const tagModeProps = React.useMemo(() => ({
        value: innerValue,
        options,
        focused,
        inputRef,
        menuRef,
        fixedHeight,
        onSetValue: setInnerValue,
        onSetOptions: setInnerOptions,
        onChangeInput: handleChangeInput,
        onFocus: handleFocusInput,
        onBlur: handleBlurInput,
        onSelect,
    }), [
        fixedHeight,
        focused,
        handleBlurInput,
        handleChangeInput,
        handleFocusInput,
        innerValue,
        onSelect,
        options,
        setInnerValue,
    ]);
    // defaultValue needs only on first render
    React.useEffect(() => {
        if (visible) {
            return;
        }
        if (isDefined(value ?? defaultValue)) {
            const { label } = getOptionByValue(value ?? defaultValue);
            if (enableCustomValue) {
                setInputValue((prevVal) => label ?? value ?? prevVal);
            }
            else {
                setInputValue((prevVal) => label ?? prevVal);
            }
        }
        if (isDefined(value)) {
            setInnerValue(value);
        }
    }, [value, visible, defaultValue, enableCustomValue, getOptionByValue, setInnerValue, setInputValue]);
    React.useEffect(() => {
        autoFocus && inputRef.current?.focus();
    }, [autoFocus]);
    React.useEffect(() => {
        if (!visible) {
            setSelected(false);
        }
    }, [visible]);
    React.useEffect(() => {
        setInnerOptions(options || []);
    }, [options]);
    // когда нет саджестов устанавливаем ширину всплывающего окна равную инпуту, чтобы EmtyContent выглядел адекватно
    React.useEffect(() => {
        if (!innerOptions.length) {
            setFixWidth.on();
        }
        else {
            fixedWidth ? setFixWidth.on() : setFixWidth.off();
        }
    }, [fixedWidth, innerOptions.length, setFixWidth]);
    React.useEffect(() => {
        if (isUndefined(value) && !isFirstRender.current) {
            setInputValue(undefined);
            setInnerValue(undefined);
        }
        isFirstRender.current = false;
    }, [value, setInnerValue]);
    return (React.createElement(Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, className: menuClassName, mode: mode === 'tags' ? 'multiple' : mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixWidth, mobile: mobile, header: header, closeOnSelect: closeOnSelect, flip: flip, optionsSort: optionsSort, emptyContent: React.createElement(EmptyContent, { inputValue: inputValue, loading: loading, emptyContent: emptyContent, onClick: handleEmptyContentClick }), tabIndex: -1, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect, onClick: handleClick },
        React.createElement("div", { "data-qa": "Autocomplete", className: cx(className, 'container'), tabIndex: -1 },
            React.createElement(InputView, { ...restProps, ref: useMergedRef(ref, inputRef), mode: mode, inputProps: mode === 'tags' ? tagModeProps : defaultModeProps }))));
});
Autocomplete.displayName = 'Autocomplete';

export { Autocomplete };
//# sourceMappingURL=Autocomplete.js.map
